import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { hotjar } from 'react-hotjar';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';
import RootErrorBoundary from './components/ErrorBoundary/RootErrorBoundary.tsx';
import { BasicHelmetHead } from './components/common/index.ts';
import { STORAGE } from './constants/projectName.ts';
import { PAGE_ROUTE } from './constants/url.ts';
import routes from './router.tsx';
import { useAuthStore } from './store/index.ts';
import useUserStore from './store/useUserStore.ts';
import { AppPushMessage } from './types/flutter.ts';
import { isEventLoggingValid } from './utils/eventTracker.ts';

export default function App() {
  const { email } = useUserStore();

  // Amplitude, GA 세팅
  useEffect(() => {
    if (!email) return;

    if (!isEventLoggingValid(email)) {
      console.log('GA, Amplitude tracking DISABLED');
      return;
    }
    // Amplitude 세팅
    amplitude.init('2c01b6126d992f5fcc306d227c198d27', {
      defaultTracking: true,
    });
    amplitude.setUserId(email); // 유저별 고유 ID - 이메일

    // GA 스크립트가 이미 로드되었는지 확인
    if (
      !document.querySelector(
        "script[src='https://www.googletagmanager.com/gtag/js?id=G-324TX7YR8X']",
      )
    ) {
      // GA 세팅
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-324TX7YR8X';
      document.head.appendChild(script);

      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        window.gtag =
          window.gtag ||
          function gtag() {
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments);
          };
        window.gtag('js', new Date());
        window.gtag('config', 'G-324TX7YR8X');
      };
    }
  }, [email]); // email이 변경될 때마다 실행

  useEffect(() => {
    const handleFlutterInAppWebViewPlatformReady = (event: Event) => {
      if (sessionStorage.getItem('flutterHandlerCalled')) return;

      // app token
      window.flutter_inappwebview
        .callHandler('appToken')
        .then(function (result) {
          console.log(result);
          sessionStorage.setItem('appToken', result);
        });

      // app info
      window.flutter_inappwebview
        .callHandler('appInfo')
        .then(function (result) {
          console.log(JSON.stringify(result));
          sessionStorage.setItem('appInfo', JSON.stringify(result));
        });

      // push Message
      window.flutter_inappwebview
        .callHandler('push')
        .then(function (result: string) {
          if (result) {
            const r1: AppPushMessage = JSON.parse(result);
            console.log('Received push data through callHandler:', result);
            sessionStorage.setItem('indexPushData', JSON.stringify(result));
            const pageUrl = r1.data.value;
            if (useAuthStore.getState().isAuthenticated) {
              window.location.href = `/${pageUrl}`;
            } else {
              sessionStorage.setItem(
                STORAGE.AUTH.REDIRECT_AFTER_LOGIN,
                pageUrl,
              );
              window.location.href = PAGE_ROUTE.SIGNIN_HOME;
            }
          }
        });

      sessionStorage.setItem('flutterHandlerCalled', 'true');
    };

    window.addEventListener(
      'flutterInAppWebViewPlatformReady',
      handleFlutterInAppWebViewPlatformReady,
    );
    return () => {
      window.addEventListener(
        'flutterInAppWebViewPlatformReady',
        handleFlutterInAppWebViewPlatformReady,
      );
    };
  }, []);

  useEffect(() => {
    window.appPushMessage = (result: string) => {
      if (result) {
        const r1: AppPushMessage = JSON.parse(result);
        sessionStorage.setItem('indexPushData', JSON.stringify(result));
        const pageUrl = r1.data.value;

        if (useAuthStore.getState().isAuthenticated) {
          window.location.href = `/${pageUrl}`;
        } else {
          sessionStorage.setItem(STORAGE.AUTH.REDIRECT_AFTER_LOGIN, pageUrl);
          window.location.href = PAGE_ROUTE.SIGNIN_HOME;
        }
      }
    };
  }, []);

  useEffect(() => {
    const setVhVariable = () => {
      setTimeout(() => {
        document.documentElement.style.setProperty(
          '--vh',
          `${window.innerHeight}px`,
        );
      }, 150);
    };

    window.addEventListener('resize', setVhVariable);
    window.addEventListener('orientationchange', setVhVariable);
    setVhVariable();

    return () => {
      window.removeEventListener('resize', setVhVariable);
      window.removeEventListener('orientationchange', setVhVariable);
    };
  }, []);

  useEffect(() => {
    // hotjar 세팅
    hotjar.initialize({
      id: import.meta.env.VITE_HOTJAR_ID,
      sv: 6,
    });
  }, []);

  const router = createBrowserRouter(routes);

  return (
    <HelmetProvider>
      <BasicHelmetHead />
      <RootErrorBoundary>
        <RouterProvider router={router} />
      </RootErrorBoundary>
    </HelmetProvider>
  );
}
